/**
 * D-096 — the storefront widget: one `<script>` tag that puts a Rive FAB on a WooCommerce shop, opening a text chat
 * and a voice call with the tenant's own agent.
 *
 *   <script async src="https://dialoai.net/widget/v1.js" data-dialo-key="wgt_..."></script>
 *
 * Design constraints that shaped this file:
 *  · **Shadow DOM, always.** A WordPress storefront loads an unknown theme plus a dozen plugins. Everything lives in
 *    a closed shadow root with its own stylesheet so the shop's CSS cannot reach in and ours cannot leak out.
 *  · **RTL and Persian first**, because every tenant is.
 *  · **Nothing is trusted from the page.** The only input is the key on the script tag.
 *  · **Voice reuses the existing contract.** `POST /public/webrtc/token` + `livekit-client`, exactly as
 *    `apps/portal-web/src/app/demo/page.tsx` does — same room naming, same `lk.transcription` text stream. The
 *    endpoint is unchanged, which is what keeps the Android client and the portal demo working.
 *  · **Degrade, never disappear.** If Rive fails to load, a CSS orb takes its place.
 */
import type { Mascot, MascotState } from './mascot';
import type { VoiceCall } from './voice';
import { STYLES } from './styles';

interface WidgetConfig {
  tenant_slug: string;
  agent_name: string;
  organization: string;
  greeting: string;
  channels: { chat: boolean; voice: boolean };
  launcher_label: string;
}

/** A transcript line. `sid` is the LiveKit segment id for a voice caption, so interim updates replace in place. */
interface Msg { who: 'user' | 'agent'; text: string; streaming?: boolean; sid?: string }

const VISITOR_KEY = 'dialo_visitor_id';

/** A stable, anonymous per-browser handle so a returning visitor keeps their thread. Not an identity, and it is
 *  wrapped because storage throws in a private window or with site data blocked. */
function visitorId(): string {
  try {
    const existing = localStorage.getItem(VISITOR_KEY);
    if (existing) return existing;
    const made = `v-${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(VISITOR_KEY, made);
    return made;
  } catch {
    return `v-${Math.random().toString(36).slice(2)}`;
  }
}

class Widget {
  private root!: ShadowRoot;
  private host!: HTMLDivElement;
  private panel!: HTMLDivElement;
  private log!: HTMLDivElement;
  private input!: HTMLTextAreaElement;
  private canvas!: HTMLCanvasElement;
  private fallbackOrb!: HTMLDivElement;
  private statusEl!: HTMLDivElement;
  private voiceBtn!: HTMLButtonElement;
  private mascot: Mascot | null = null;

  private open = false;
  private messages: Msg[] = [];
  private sessionId: string | null = null;
  private sending = false;
  private voice: VoiceCall | null = null;
  private config: WidgetConfig | null = null;
  /** D-10x — set only when the shop's WordPress says this visitor is logged in; null for every anonymous visitor
   *  and for every shop still on a plugin version that doesn't emit `data-dialo-nonce` at all. */
  private customerToken: string | null = null;

  /** D-10x, headless storefronts — an externally-supplied identity, for a host page that already knows who the
   *  visitor is through its OWN login (e.g. a Next.js storefront in front of a headless WooCommerce backend,
   *  where there is no WordPress-rendered page to carry a nonce at all). The host mints the token server-to-server
   *  exactly as the WP plugin does (same `/public/license/customer-token` call, same license key) and hands it
   *  over via `window.DialoWidget.identify(...)` — see `boot()`. Called any time after boot; nothing here is
   *  time-sensitive since the token is only read when a chat or voice session actually starts. */
  setCustomerToken(token: string): void {
    this.customerToken = token;
  }

  constructor(private readonly apiBase: string, private readonly assetBase: string, private readonly key: string,
              private readonly wpNonce?: string, private readonly wpOrigin?: string) {}

  async start(): Promise<void> {
    let cfg: WidgetConfig;
    try {
      const r = await fetch(`${this.apiBase}/public/widget/config`, { headers: { 'x-dialo-key': this.key } });
      if (!r.ok) throw new Error(String(r.status));
      cfg = (await r.json()) as WidgetConfig;
    } catch {
      // A wrong key, an unlisted domain or a suspended plan: render nothing at all. A broken bubble on a live
      // storefront is worse than no bubble.
      return;
    }
    this.config = cfg;
    void this.fetchCustomerToken(); // never blocks the FAB; a slow/failed fetch just means "anonymous"
    this.mount(cfg);
  }

  /** D-10x — the WP plugin's own REST route (never ours): only WordPress's cookie session can answer "is this
   *  visitor logged in". On a normal install the widget is embedded directly on the WordPress site, so this is a
   *  same-origin call. A headless storefront (the page running this script is a DIFFERENT origin from the
   *  WordPress backend, e.g. Sarv: the widget runs on sarv.dialoai.net but WordPress is cms.sarv.dialoai.net) sets
   *  `data-dialo-wp-origin` on the script tag instead, and the fetch goes cross-origin WITH credentials — which
   *  only carries the WP session cookie if that cookie actually reached this browser for the WP origin in the
   *  first place (store-maker's OTP login does not do that today; see docs/16, "headless identity bridge").
   *  A 401 (logged out) or a missing nonce (an older plugin, or no bridge yet) both just leave `customerToken`
   *  null — every downstream call already tolerates that. */
  private async fetchCustomerToken(): Promise<void> {
    if (!this.wpNonce) return;
    const url = this.wpOrigin ? `${this.wpOrigin.replace(/\/$/, '')}/wp-json/dialo/v1/customer-token`
                              : '/wp-json/dialo/v1/customer-token';
    try {
      const r = await fetch(url, {
        headers: { 'X-WP-Nonce': this.wpNonce },
        credentials: this.wpOrigin ? 'include' : 'same-origin',
      });
      if (!r.ok) return;
      const body = (await r.json()) as { customer_token?: string };
      if (body.customer_token) this.customerToken = body.customer_token;
    } catch { /* anonymous is the safe default */ }
  }

  // ---- DOM ---------------------------------------------------------------

  private mount(cfg: WidgetConfig): void {
    this.host = document.createElement('div');
    this.host.setAttribute('data-dialo-widget', '');
    // `closed` so the page cannot reach in and read a visitor's conversation.
    this.root = this.host.attachShadow({ mode: 'closed' });
    const style = document.createElement('style');
    style.textContent = STYLES;
    this.root.appendChild(style);

    const wrap = document.createElement('div');
    wrap.className = 'dialo';
    wrap.setAttribute('dir', 'rtl');
    wrap.setAttribute('lang', 'fa');
    wrap.innerHTML = `
      <button class="fab" type="button" aria-label="${esc(cfg.launcher_label)}" title="${esc(cfg.launcher_label)}">
        <canvas class="orb" width="120" height="120" aria-hidden="true"></canvas>
        <span class="orb-fallback" aria-hidden="true"></span>
      </button>
      <section class="panel" role="dialog" aria-modal="false" aria-label="${esc(cfg.launcher_label)}" hidden>
        <header class="head">
          <div class="who">
            <strong>${esc(cfg.agent_name)}</strong>
            <span class="org">${esc(cfg.organization)}</span>
          </div>
          <div class="actions">
            <button class="voice" type="button" title="گفت‌وگوی صوتی">${MIC_SVG}</button>
            <button class="close" type="button" aria-label="بستن">${X_SVG}</button>
          </div>
        </header>
        <div class="status" role="status" aria-live="polite"></div>
        <div class="log" tabindex="0"></div>
        <form class="composer">
          <textarea rows="1" placeholder="پیام‌تان را بنویسید…" aria-label="پیام" maxlength="1000"></textarea>
          <button class="send" type="submit" aria-label="ارسال">${SEND_SVG}</button>
        </form>
        <div class="footer">دستیار هوشمند — پاسخ‌ها ممکن است کامل نباشد</div>
      </section>`;
    this.root.appendChild(wrap);
    document.body.appendChild(this.host);

    this.panel = wrap.querySelector('.panel') as HTMLDivElement;
    this.log = wrap.querySelector('.log') as HTMLDivElement;
    this.input = wrap.querySelector('textarea') as HTMLTextAreaElement;
    this.canvas = wrap.querySelector('.orb') as HTMLCanvasElement;
    this.fallbackOrb = wrap.querySelector('.orb-fallback') as HTMLDivElement;
    this.statusEl = wrap.querySelector('.status') as HTMLDivElement;
    this.voiceBtn = wrap.querySelector('.voice') as HTMLButtonElement;

    (wrap.querySelector('.fab') as HTMLButtonElement).addEventListener('click', () => this.toggle());
    (wrap.querySelector('.close') as HTMLButtonElement).addEventListener('click', () => this.toggle(false));
    this.voiceBtn.addEventListener('click', () => void this.toggleVoice());
    (wrap.querySelector('.composer') as HTMLFormElement).addEventListener('submit', (e) => {
      e.preventDefault();
      void this.send();
    });
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void this.send(); }
    });
    this.input.addEventListener('input', () => {
      this.input.style.height = 'auto';
      this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.open) this.toggle(false); });
    // A closing tab is the normal way a storefront chat ends. `sendBeacon` survives unload where `fetch` does not;
    // the server also reaps idle sessions, so this is a courtesy, never a requirement.
    window.addEventListener('pagehide', () => {
      if (!this.sessionId) return;
      try {
        navigator.sendBeacon(`${this.apiBase}/chat/end`,
          new Blob([JSON.stringify({ session_id: this.sessionId })], { type: 'application/json' }));
      } catch { /* nothing to do on unload */ }
    });
    if (!cfg.channels.voice) this.voiceBtn.hidden = true;

    void this.initMascot();
    this.push({ who: 'agent', text: cfg.greeting });
  }

  /** The Rive runtime is 73 KB gzipped, so it arrives in its own chunk AFTER the bubble is already on screen and
   *  animating as a CSS orb. The upgrade is invisible if it lands quickly and harmless if it never does. */
  private async initMascot(): Promise<void> {
    let ok = false;
    try {
      const { Mascot } = await import('./mascot');
      this.mascot = new Mascot(this.canvas, `${this.assetBase}/mascot.riv`);
      ok = await this.mascot.load();
    } catch {
      ok = false; // the chunk itself could not be fetched (offline, blocked, 404)
    }
    if (!ok) {
      // The CSS orb that `demo/page.tsx` uses. Same states, no WASM.
      this.mascot = null;
      this.canvas.hidden = true;
      this.fallbackOrb.classList.add('on');
    }
    this.setState('idle');
  }

  private setState(state: MascotState): void {
    this.mascot?.set(state);
    this.fallbackOrb.dataset.state = state;
    this.host.dataset.state = state;
  }

  private toggle(force?: boolean): void {
    this.open = force ?? !this.open;
    this.panel.hidden = !this.open;
    this.host.dataset.open = String(this.open);
    if (this.open) {
      this.mascot?.jump();
      this.mascot?.resize();
      this.input.focus();
      this.scroll();
    } else if (this.voice?.live) {
      // Closing the panel (the X button, or Escape) must end an active call — otherwise the agent keeps talking
      // into a conversation nobody can see any more, and the next tap of the mic opens a SECOND room instead of
      // resuming this one, since the visitor has no way left to reach the "live" one to hang it up.
      void this.voice.stop();
    }
  }

  // ---- text channel -------------------------------------------------------

  private async ensureSession(): Promise<string | null> {
    if (this.sessionId) return this.sessionId;
    try {
      const r = await fetch(`${this.apiBase}/chat/start`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-dialo-key': this.key },
        body: JSON.stringify({
          visitor_id: visitorId(), page_url: location.href.slice(0, 500),
          ...(this.customerToken ? { customer_token: this.customerToken } : {}),
        }),
      });
      if (!r.ok) throw new Error(String(r.status));
      const body = (await r.json()) as { session_id: string };
      this.sessionId = body.session_id;
      return this.sessionId;
    } catch {
      this.status('اتصال برقرار نشد. لطفاً بعداً دوباره تلاش کنید.');
      return null;
    }
  }

  private async send(): Promise<void> {
    const text = this.input.value.trim();
    if (!text || this.sending) return;
    this.input.value = '';
    this.input.style.height = 'auto';
    this.push({ who: 'user', text });
    this.sending = true;
    this.setState('thinking');
    this.status('');

    const sid = await this.ensureSession();
    if (!sid) { this.sending = false; this.setState('idle'); return; }

    const bubble: Msg = { who: 'agent', text: '', streaming: true };
    this.push(bubble);

    try {
      const res = await fetch(`${this.apiBase}/chat/send`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'x-dialo-key': this.key },
        body: JSON.stringify({ session_id: sid, text }),
      });
      if (!res.ok || !res.body) throw new Error(String(res.status));
      await this.readSse(res.body, bubble);
    } catch {
      bubble.text = bubble.text || 'خطایی رخ داد؛ لطفاً دوباره بفرستید.';
    } finally {
      bubble.streaming = false;
      this.sending = false;
      this.setState('idle');
      this.render();
    }
  }

  /** Minimal SSE reader: the chat service sends `event:`/`data:` pairs separated by a blank line. */
  private async readSse(body: ReadableStream<Uint8Array>, bubble: Msg): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const frames = buf.split('\n\n');
      buf = frames.pop() ?? '';
      for (const frame of frames) {
        let event = 'message';
        const data: string[] = [];
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:')) data.push(line.slice(5).trim());
        }
        if (!data.length) continue;
        let payload: { text?: string; name?: string; message?: string };
        try { payload = JSON.parse(data.join('\n')); } catch { continue; }

        if (event === 'delta' && payload.text) {
          bubble.text += payload.text;
          this.setState('speaking');
          this.render();
        } else if (event === 'tool') {
          this.status(`در حال بررسی…`);
        } else if (event === 'done') {
          // The server's cleaned text is authoritative: the deltas are raw and may contain a trailing repeat the
          // chat filter removed.
          if (payload.text) bubble.text = payload.text;
          this.status('');
        } else if (event === 'error') {
          bubble.text = payload.message || 'خطایی رخ داد.';
          this.status('');
        }
      }
    }
  }

  // ---- voice channel ------------------------------------------------------

  /** Loads `livekit-client` (143 KB gz) only now, the first time someone actually wants to talk. */
  private async toggleVoice(): Promise<void> {
    if (this.voice?.live) { await this.voice.stop(); return; }
    if (!this.config) return;
    this.voiceBtn.classList.add('live');
    this.voiceBtn.disabled = true;
    try {
      if (!this.voice) {
        const { VoiceCall } = await import('./voice');
        this.voice = new VoiceCall(this.apiBase, this.config.tenant_slug, this.customerToken, {
          audioHost: this.root,
          onState: (s) => this.setState(s),
          onStatus: (t) => this.status(t),
          onCaption: (id, who, text) => this.caption(id, who, text),
          onEnded: () => this.voiceBtn.classList.remove('live'),
        });
      }
      const ok = await this.voice.start();
      if (!ok) this.voiceBtn.classList.remove('live');
    } catch {
      this.status('بخش صوتی بارگذاری نشد.');
      this.voiceBtn.classList.remove('live');
      this.setState('idle');
    } finally {
      this.voiceBtn.disabled = false;
    }
  }

  /** Upserts one caption line: interim updates of the same utterance share an id. */
  private caption(id: string, who: Msg['who'], text: string): void {
    const line = this.messages.find((m) => m.sid === id);
    if (line) { line.text = text; this.render(); return; }
    this.push({ who, text, sid: id });
  }

  // ---- rendering ----------------------------------------------------------

  private push(m: Msg): void { this.messages.push(m); this.render(); }

  private status(text: string): void {
    this.statusEl.textContent = text;
    this.statusEl.classList.toggle('on', Boolean(text));
  }

  private render(): void {
    this.log.innerHTML = this.messages
      .filter((m) => m.text.trim() || m.streaming)
      .map((m) => `<div class="msg ${m.who}${m.streaming && !m.text ? ' typing' : ''}">${
        m.text.trim() ? linkify(esc(m.text)) : '<span class="dots"><i></i><i></i><i></i></span>'
      }</div>`)
      .join('');
    this.scroll();
  }

  private scroll(): void { this.log.scrollTop = this.log.scrollHeight; }
}

/** Everything the agent writes is escaped before it reaches the DOM; only then are bare URLs linkified. The order
 *  matters — linkifying first would let a crafted product name inject an attribute. */
function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function linkify(escaped: string): string {
  return escaped.replace(/https?:\/\/[^\s<]+/g, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer nofollow">${url}</a>`);
}

const MIC_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 2a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/><path d="M19 10v1a7 7 0 0 1-14 0v-1M12 18v4"/></svg>';
const X_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
const SEND_SVG = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4 3 11l6 2 2 6 9-15z"/></svg>';

/** Boot. The script tag carries the key; its own src gives us the API and asset origin, so a store never configures
 *  a URL and a self-hosted deployment works with no change. */
function boot(): void {
  const el = (document.currentScript as HTMLScriptElement | null)
    ?? document.querySelector<HTMLScriptElement>('script[data-dialo-key]');
  const key = el?.dataset.dialoKey?.trim();
  if (!key) return;
  if (document.querySelector('[data-dialo-widget]')) return; // a theme that injects the tag twice
  let origin = 'https://dialoai.net';
  try { origin = new URL(el!.src, location.href).origin; } catch { /* keep the default */ }
  const apiBase = el?.dataset.dialoApi?.replace(/\/$/, '') || `${origin}/api`;
  const assetBase = el?.dataset.dialoAssets?.replace(/\/$/, '') || `${origin}/widget`;
  // D-10x — only a plugin install updated for logged-in-customer awareness sets this; its absence just means the
  // widget never attempts the same-origin WordPress lookup and every visitor stays anonymous, as before.
  const wpNonce = el?.dataset.dialoNonce?.trim() || undefined;
  // D-10x — only set on a headless storefront, where WordPress is a different origin from the page this script
  // runs on. Absent, the customer-token lookup stays same-origin exactly as before (zero change for every normal
  // WordPress-embedded install).
  const wpOrigin = el?.dataset.dialoWpOrigin?.trim().replace(/\/$/, '') || undefined;
  const widget = new Widget(apiBase, assetBase, key, wpNonce, wpOrigin);
  // D-10x, headless storefronts — exposed so the host page's own login code can call
  // `window.DialoWidget.identify(token)` once it has minted a customer_token through its own backend. Safe to call
  // before or after the widget has finished booting.
  (window as unknown as { DialoWidget?: { identify(token: string): void } }).DialoWidget = {
    identify: (token: string) => widget.setCustomerToken(token),
  };
  const go = () => void widget.start();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', go);
  else go();
}

boot();
